import { useMemo } from 'react'
import { CalendarCheck } from 'lucide-react'
import { getSittingsForExam, formatSittingDate, type ExamSitting } from '@/data/examSittings'

interface Props {
  examId: string
  /** Currently selected exam date (ISO YYYY-MM-DD) — highlights matching sitting */
  selectedDate?: string
  /** Called with the end date (or start date for single-day sittings) when a row is clicked, and the row's sitting */
  onSelect: (date: string, sitting: ExamSitting) => void
}

function isSelected(sitting: ExamSitting, selectedDate: string | undefined): boolean {
  if (!selectedDate) return false
  if (sitting.endDate) {
    return selectedDate >= sitting.startDate && selectedDate <= sitting.endDate
  }
  return selectedDate === sitting.startDate
}

export function ExamSittingsList({ examId, selectedDate, onSelect }: Props) {
  const sittings = useMemo(() => getSittingsForExam(examId), [examId])

  if (sittings.length === 0) return null

  return (
    <div className="space-y-1">
      <p className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground flex items-center gap-1">
        <CalendarCheck className="h-3 w-3" />
        Upcoming sittings
      </p>
      <div className="flex flex-col gap-1.5">
        {sittings.map((s, i) => {
          const active = isSelected(s, selectedDate)
          const selectDate = s.endDate ?? s.startDate
          return (
            <button
              key={i}
              type="button"
              onClick={() => onSelect(selectDate, s)}
              className={`w-full text-left rounded-lg px-3 py-2.5 transition-all duration-150 ${
                active
                  ? 'bg-primary/10 text-foreground shadow-sm'
                  : 'bg-muted/40 hover:bg-accent/60 text-muted-foreground hover:text-foreground'
              }`}
            >
              <div className="flex items-center justify-between gap-2">
                <span className="text-sm font-semibold">{formatSittingDate(s)}</span>
                <span className={`shrink-0 rounded px-1.5 py-0.5 text-xs font-semibold tracking-wide ${
                  s.format === 'CBT'
                    ? 'bg-blue-100 text-blue-700 dark:bg-blue-950 dark:text-blue-300'
                    : s.format === 'P/P'
                      ? 'bg-amber-100 text-amber-700 dark:bg-amber-950 dark:text-amber-300'
                      : 'bg-muted text-muted-foreground'
                }`}>
                  {s.format}
                </span>
              </div>
              {s.registrationDeadline && (
                <p className="mt-0.5 text-xs text-muted-foreground">
                  Reg. deadline: {formatSittingDate({ ...s, startDate: s.registrationDeadline, endDate: null })}
                </p>
              )}
            </button>
          )
        })}
      </div>
    </div>
  )
}
